import { useState, useRef, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useApiQuery, useApiMutation } from "@/lib/api/hooks"
import { CardRendererRaw } from "@/components/cards/CardRenderer"
import { Button } from "@/components/ui/button"
import { playCorrect, playIncorrect } from "@/lib/sounds"
import { Scratchpad } from "@/components/Scratchpad"

interface CardResult {
  cardId: string
  correct: boolean
  responseTimeMs: number
}

export function TestPage() {
  const { unitId } = useParams<{ unitId: string }>()
  const navigate = useNavigate()
  const { data, isPending } = useApiQuery(
    "get",
    "/units/{unitId}/test",
    { params: { path: { unitId: unitId! } } },
    { enabled: !!unitId }
  )
  const completeMutation = useApiMutation("post", "/units/{unitId}/test/complete")

  const [currentIndex, setCurrentIndex] = useState(0)
  const [results, setResults] = useState<CardResult[]>([])
  const [answered, setAnswered] = useState(false)
  const [finished, setFinished] = useState(false)
  const cardStartTime = useRef(Date.now())

  const cards = data?.cards ?? []
  const currentCard = cards[currentIndex]
  const totalCards = cards.length
  const correctCount = results.filter((r) => r.correct).length

  const handleAnswer = useCallback(
    (correct: boolean) => {
      if (!currentCard || answered) return
      const elapsed = Date.now() - cardStartTime.current
      setResults((prev) => [
        ...prev,
        { cardId: currentCard.id, correct, responseTimeMs: elapsed },
      ])
      setAnswered(true)
      if (correct) playCorrect()
      else playIncorrect()
    },
    [currentCard, answered]
  )

  function handleContinue() {
    if (currentIndex < totalCards - 1) {
      setCurrentIndex((i) => i + 1)
      setAnswered(false)
      cardStartTime.current = Date.now()
    } else {
      setFinished(true)
      if (data?.test) {
        completeMutation.mutate({
          params: { path: { unitId: unitId! } },
          body: {
            testId: data.test.id,
            correctCount,
            totalCount: totalCards,
            cardResults: results,
          },
        })
      }
    }
  }

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    )
  }

  if (!data || cards.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center flex-col gap-4">
        <p className="text-sm text-muted-foreground">No test available for this unit.</p>
        <Button variant="outline" onClick={() => navigate(`/unit/${unitId}`)}>
          Go back
        </Button>
      </div>
    )
  }

  if (finished) {
    const result = completeMutation.data
    const accuracy = totalCards > 0 ? Math.round((correctCount / totalCards) * 100) : 0
    const passed = result?.passed ?? accuracy >= (data.test.passThreshold ?? 80)

    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="max-w-sm w-full text-center space-y-6 px-4">
          <h2 className="text-2xl font-bold">
            {passed ? "Test Passed" : "Test Failed"}
          </h2>
          <p className="text-muted-foreground">{data.unitName} — Unit Test</p>
          <div className={`text-5xl font-bold tabular-nums ${passed ? "text-green-600" : "text-red-500"}`}>
            {result?.accuracy ?? accuracy}%
          </div>
          <p className="text-sm text-muted-foreground">
            {correctCount} of {totalCards} correct — need {data.test.passThreshold}% to pass
          </p>
          {passed && (
            <p className="text-sm font-medium text-green-600">
              Unit complete! You've mastered all topics in {data.unitName}.
            </p>
          )}
          {!passed && (
            <p className="text-sm text-muted-foreground">
              Review the topics and try again.
            </p>
          )}
          <div className="flex flex-col gap-2 pt-4">
            {!passed && (
              <Button onClick={() => {
                setCurrentIndex(0)
                setResults([])
                setAnswered(false)
                setFinished(false)
                cardStartTime.current = Date.now()
              }}>
                Retry test
              </Button>
            )}
            <Button variant={passed ? "default" : "outline"} onClick={() => navigate(`/unit/${unitId}`)}>
              Back to unit
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border px-4 py-3 flex items-center gap-4 shrink-0">
        <button
          onClick={() => navigate(`/unit/${unitId}`)}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Exit
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">Unit Test: {data.unitName}</p>
        </div>
        <span className="text-xs text-muted-foreground tabular-nums shrink-0">
          {currentIndex + 1} / {totalCards}
        </span>
      </header>

      <div className="h-1 bg-muted shrink-0">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / totalCards) * 100}%` }}
        />
      </div>

      <main className="flex-1 overflow-y-auto">
        <div className="relative max-w-2xl mx-auto px-4 py-8">
          <Scratchpad resetKey={currentCard?.id} />
          {currentCard && (
            <CardRendererRaw
              key={currentCard.id}
              type={currentCard.type}
              data={currentCard.data as Record<string, unknown>}
              onAnswer={handleAnswer}
            />
          )}
        </div>
      </main>

      <div className="border-t border-border px-4 py-4 shrink-0">
        <div className="max-w-2xl mx-auto">
          {answered && (
            <Button className="w-full" onClick={handleContinue}>
              {currentIndex < totalCards - 1 ? "Continue" : "Finish"}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
